import React from "react";
import { Card, Col, Row } from "react-bootstrap";
import styled from "styled-components";

import { HorizontalDivider } from "../../../common/components/core";
import { useSelector } from "../../../redux/hooks";
import { currentRolesSelector } from "../../../redux/slices/auth/selectors";
import {
    PaymentStatus,
    PreAndPaymentStateWithDocuments,
} from "../../../types/payment";
import { formatAmount } from "../../../utils/amount";
import { formatToDate } from "../../../utils/common";

import PaymentStatusBadge from "./PaymentStatusBadge";
import { isUserFinanceDepartment } from "../../../utils/role";
import { ltaErrorCodeMapping } from "../../../utils/errorHandling";

type PaymentReceiptDetailProps = {
    preAndPayment: PreAndPaymentStateWithDocuments;
};

const PaymentReceiptDetail = ({
    preAndPayment,
}: PaymentReceiptDetailProps): JSX.Element => {
    const { paymentState } = preAndPayment;
    const currentRoles = useSelector(currentRolesSelector);
    const isFailed =
        paymentState?.status === PaymentStatus.FAILED &&
        !isUserFinanceDepartment(currentRoles);

    return (
        <Card
            className={`mb-5 border-0 ${
                isFailed ? "text-placeholder-grey" : ""
            }`}
        >
            <Card.Header className="p-0 pb-3">
                <h2 className="mb-0">
                    Payment Receipt{" "}
                    {paymentState?.settlementBankRef && (
                        <span className="fs-7 fw-bold">
                            #{paymentState.settlementBankRef}
                        </span>
                    )}
                    {paymentState?.status && (
                        <div className="mt-1 fs-7 fw-normal float-end">
                            <PaymentStatusBadge
                                paymentStatus={paymentState?.status}
                                roles={currentRoles}
                                isPaymentReceipt={true}
                            />
                        </div>
                    )}
                </h2>
            </Card.Header>
            <Card.Body className="px-0 py-0">
                <div>
                    <StyledH3 isFailed={isFailed}>
                        Account Payable Details
                    </StyledH3>
                    <p className="mb-2">
                        <span className="h1">
                            {formatAmount(paymentState?.instructedMVUnit ?? 0)}
                        </span>
                        <span className="ms-2 fw-bold">
                            {paymentState?.instructedMVCurrency}
                        </span>
                    </p>
                    <StyledLabel className="mb-5" isFailed={isFailed}>
                        Paid Amount
                    </StyledLabel>
                </div>
                <HorizontalDivider />
                <Row xs={3}>
                    <Col className="border-end border-humble-grey">
                        <StyledLabel
                            className="mt-1 mb-2"
                            isFailed={isFailed}
                        >
                            Bank Transaction Date
                        </StyledLabel>
                        {paymentState?.status === PaymentStatus.COMPLETED &&
                            paymentState?.settlementDate && (
                                <p className="fw-bold">
                                    {formatToDate(paymentState?.settlementDate)}
                                </p>
                            )}
                        {paymentState?.status === PaymentStatus.FAILED &&
                            paymentState?.rejectDate && (
                                <p className="fw-bold">
                                    {formatToDate(paymentState?.rejectDate)}
                                </p>
                            )}
                    </Col>
                    <Col className="px-4">
                        <StyledLabel
                            className="mt-1 mb-2"
                            isFailed={isFailed}
                        >
                            Payment Order ID
                        </StyledLabel>
                        <p className="fw-bold">
                            {paymentState?.customerRefNumber}
                        </p>
                    </Col>
                </Row>

                <HorizontalDivider className="mb-0" />
                {paymentState?.rejectReason && paymentState?.rejectCode && (
                    <>
                        <StyledLabel className="mt-5 mb-2">
                            Reject Reason
                        </StyledLabel>
                        <p className="text-pre-wrap fw-bold mb-0">
                            {ltaErrorCodeMapping(paymentState.rejectCode, paymentState.rejectReason)}
                        </p>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

const StyledH3 = styled.h3<{ isFailed?: boolean }>`
    margin-bottom: 1rem;
    color: ${(props) =>
        props.isFailed ? "var(--bs-placeholder-grey)" : "var(--bs-body-color)"};
`;

const StyledLabel = styled.p<{ isFailed?: boolean }>`
    color: ${(props) =>
        props.isFailed ? "var(--bs-placeholder-grey)" : "var(--bs-label-grey)"};
`;

export default PaymentReceiptDetail;
